import { Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { useScrollReveal } from '../hooks/useScrollReveal';

const SERVICES = [
  {
    icon: '🌐', title: 'Web Development',
    headline: 'WEBSITES THAT PERFORM, NOT JUST IMPRESS.',
    desc: 'We build fast, scalable, conversion-optimized websites using modern technologies. From landing pages to complex web applications — we handle it all.',
    features: ['Custom website development','WordPress & Shopify','React, Next.js & modern frameworks','REST API & backend development','Performance & speed optimization','Mobile-first responsive design'],
    img: require('../images/b1.webp'),
    imgAlt: 'Web development code on screen',
    tag: 'Most Popular',
  },
  {
    icon: '🎨', title: 'Web Design',
    headline: 'DESIGN THAT CONVERTS VISITORS INTO CUSTOMERS.',
    desc: 'Great design is not just about looking good — it is about guiding users to take action. We create premium UI/UX designs that reflect your brand and drive measurable results.',
    features: ['UI/UX design & wireframing','Brand identity & logo design','Figma prototyping & design systems','Landing page design','Conversion rate optimized layouts','Motion & interaction design'],
    img: require('../images/b2.webp'),
    imgAlt: 'UI UX design on tablet',
    tag: null,
  },
  {
    icon: '📱', title: 'App Development',
    headline: 'MOBILE APPS YOUR USERS WILL LOVE.',
    desc: 'From idea to App Store — we build cross-platform mobile apps for iOS and Android that are fast, reliable, and built to scale with your business.',
    features: ['React Native cross-platform apps','Flutter app development','iOS & Android native apps','App UI/UX design','App Store & Play Store deployment','Ongoing maintenance & updates'],
    img: require('../images/b3.webp'),
    imgAlt: 'Mobile app development',
    tag: null,
  },
  {
    icon: '🔍', title: 'SEO',
    headline: 'RANK HIGHER. GET FOUND. GROW ORGANICALLY.',
    desc: 'We help your business climb search rankings and stay there. Our data-driven SEO strategies are built for long-term growth — not quick hacks that fade.',
    features: ['Technical SEO audit & fixes','On-page & off-page optimization','Keyword research & strategy','Local SEO & Google My Business','Content strategy & creation','Monthly reporting & analysis'],
    img: require('../images/b4.webp'),
    imgAlt: 'SEO analytics dashboard',
    tag: null,
  },
  {
    icon: '📣', title: 'Digital Marketing',
    headline: 'CLICKS THAT TURN INTO ACTUAL CUSTOMERS.',
    desc: 'We run full-funnel digital marketing campaigns across search, social, and email. Every rupee you spend is tracked, optimized, and working toward your business goals.',
    features: ['Google Ads & PPC campaigns','Meta (Facebook & Instagram) Ads','Social media management','Email marketing & automation','Content marketing & blogs','Analytics & conversion tracking'],
    img: require('../images/b5.webp'),
    imgAlt: 'Digital marketing strategy',
    tag: null,
  },
];

const PROCESS = [
  { step:'01', title:'Discovery', desc:'We understand your business, goals, and target audience before touching anything.' },
  { step:'02', title:'Strategy', desc:'Clear roadmap — scope, timeline, deliverables, and success metrics agreed upfront.' },
  { step:'03', title:'Execution', desc:'Our team builds. You get regular updates and working previews throughout.' },
  { step:'04', title:'Launch', desc:'We test everything thoroughly. Launch day is smooth, not stressful.' },
  { step:'05', title:'Grow', desc:'Post-launch analytics, support, and ongoing improvements to keep growing.' },
];

const FAQS = [
  { q:'How long does a website take to build?', a:'A standard business website takes 2–4 weeks. Complex web apps or e-commerce platforms take 4–8 weeks depending on features and scope.' },
  { q:'Do you work with small businesses?', a:'Absolutely. Many of our clients are small businesses and startups. We offer flexible packages to suit different budgets and goals.' },
  { q:'Will my website be mobile-friendly?', a:'Every website we build is fully responsive and mobile-first. Over 70% of web traffic is mobile — we take that seriously.' },
  { q:'Do you provide ongoing maintenance?', a:'Yes. We offer monthly maintenance packages that include updates, backups, performance checks, and priority support.' },
  { q:'How do you handle SEO for new websites?', a:'Every website we build includes basic on-page SEO setup — meta tags, schema, sitemap, and page speed optimization — as standard.' },
];

export default function Services() {
  useScrollReveal();
  return (
    <>
      <SEO title="IT Services — Web Development, Design, App, SEO & Digital Marketing" description="CodeViora offers web development, web design, app development, SEO, and digital marketing. End-to-end IT solutions for businesses that want to grow." canonical="/services" />

      {/* Hero */}
      <section className="bg-grid-black pt-24 pb-20 md:pt-32 md:pb-24 relative overflow-hidden">
        <div className="container-max relative">
          <div className="max-w-3xl animate-fade-up">
            <span className="section-label-inv">Our Services</span>
            <h1 className="section-title-inv text-5xl md:text-6xl mt-2">EVERYTHING YOUR BUSINESS NEEDS ONLINE.</h1>
            <p className="section-sub-inv text-lg">Web development, design, app development, SEO, and digital marketing — all under one roof.</p>
            <div className="flex flex-wrap gap-4 mt-8">
              <Link to="/contact" className="btn-primary-inv">Get Free Quote</Link>
              <Link to="/about" className="btn-outline-inv">About Us</Link>
            </div>
          </div>
        </div>
      </section>

      {/* Services alternating */}
      {SERVICES.map(({ icon, title, headline, desc, features, img, imgAlt, tag }, i) => (
        <section key={title} className={i % 2 === 0 ? 'bg-grid-white py-16 md:py-24' : 'bg-grid-black py-16 md:py-24'}>
          <div className="container-max">
            <div className={`grid lg:grid-cols-2 gap-12 items-center`}>
              <div className={`reveal ${i % 2 !== 0 ? 'lg:order-2' : ''}`}>
                <div className="flex items-center gap-3 mb-3">
                  <span className="text-3xl">{icon}</span>
                  {tag && <span className={`text-xs font-bold px-3 py-1 rounded-full ${i%2===0?'bg-black text-white':'bg-white text-black'}`}>{tag}</span>}
                </div>
                <span className={i%2===0?'section-label':'section-label-inv'}>{title}</span>
                <h2 className={`mt-1 text-3xl md:text-4xl leading-tight ${i%2===0?'section-title':'section-title-inv'}`}>{headline}</h2>
                <p className={`mt-4 text-sm leading-relaxed ${i%2===0?'text-black/60':'text-white/50'}`}>{desc}</p>
                <ul className="mt-6 flex flex-col gap-2.5">
                  {features.map(f => (
                    <li key={f} className="flex items-start gap-3 text-sm">
                      <span className={`w-5 h-5 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5 ${i%2===0?'bg-black/10 border border-black/20':'bg-white/10 border border-white/20'}`}>
                        <svg width="10" height="10" viewBox="0 0 10 10" fill="none" className={i%2===0?'text-black':'text-white'}>
                          <path d="M2 5l2 2 4-4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                        </svg>
                      </span>
                      <span className={i%2===0?'text-black/70':'text-white/60'}>{f}</span>
                    </li>
                  ))}
                </ul>
                <div className="mt-8">
                  <Link to="/contact" className={i%2===0?'btn-primary':'btn-primary-inv'}>Get Started →</Link>
                </div>
              </div>
              <div className={`reveal ${i%2!==0?'lg:order-1':''}`} style={{transitionDelay:'100ms'}}>
                <img src={img} alt={imgAlt} className="rounded-2xl w-full h-72 md:h-80 object-cover border border-white/10" loading="lazy" />
              </div>
            </div>
          </div>
        </section>
      ))}

      {/* Process */}
      <section className="bg-grid-black py-20 md:py-28">
        <div className="container-max">
          <div className="reveal text-center mb-14">
            <span className="section-label-inv">How We Work</span>
            <h2 className="section-title-inv">OUR PROCESS.</h2>
            <p className="section-sub-inv mx-auto">Simple, transparent, and built around getting things done right.</p>
          </div>
          <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-5">
            {PROCESS.map(({ step, title, desc }, i) => (
              <div key={step} className="reveal card-black p-6 relative" style={{transitionDelay:`${i*60}ms`}}>
                <p className="text-6xl text-white/5 absolute top-3 right-4" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{step}</p>
                <p className="text-white/40 font-bold text-xs mb-3 relative z-10">{step}</p>
                <h3 className="text-white mb-2 relative z-10" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>{title}</h3>
                <p className="text-xs text-white/40 leading-relaxed relative z-10">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="bg-grid-white py-20 md:py-28">
        <div className="container-max max-w-3xl">
          <div className="reveal text-center mb-12">
            <span className="section-label">FAQ</span>
            <h2 className="section-title">COMMON QUESTIONS.</h2>
          </div>
          <div className="flex flex-col gap-4">
            {FAQS.map(({ q, a }, i) => (
              <div key={q} className="reveal card-white p-7" style={{transitionDelay:`${i*60}ms`}}>
                <h3 className="font-semi-bold text-black mb-3 flex items-start gap-3 text-sm">
                  <span className="text-black/30 flex-shrink-0 font-normal">Q.</span>{q}
                </h3>
                <p className="text-sm text-black/50 leading-relaxed pl-5">{a}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-pure-black py-20 border-t border-white/10">
        <div className="container-max text-center reveal">
          <h2 className="text-4xl md:text-5xl text-white mb-5" style={{fontFamily:'Anton,sans-serif',fontWeight:500}}>LET'S BUILD SOMETHING.</h2>
          <p className="text-white/40 text-lg mb-8 max-w-lg mx-auto">Tell us what you need. We'll get back within 24 hours with a free consultation and quote.</p>
          <Link to="/contact" className="btn-primary-inv">
            Get Free Quote
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none"><path d="M3 9H15M11 5l4 4-4 4" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round"/></svg>
          </Link>
        </div>
      </section>
    </>
  );
}
